// Project imports
import { activityFlatsOfModels } from '@components/pages/activity-log/services/activity-log-flat.util';
import { HostActivityLogService } from '@components/pages/short-term-rental/shared/host-activity-log.service';
import { CountrySelectComponent } from '@components/shared/country-select/country-select.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { IFlatModel } from '@services/api/flat/flat-model.interface';
import { ParkingRequestClientService } from '@services/api/parking-request/parking-request-client.service';
import { IParkingRequestModel } from '@services/api/parking-request/parking-request-model.interface';
import { LogService } from '@services/util/log.service';
import { SelectItemLoaderService, UiSelectItem } from '@services/util/select-item-loader.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
// PrimeNG imports
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, input, OnInit, Output, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  ParkingActivitySnapshot,
  parkingFormSnapshotOf,
  parkingRequestCreateMetadata,
  parkingRequestDeleteMetadata,
  parkingRequestUpdateEvents
} from '../../services/parking-activity.metadata';
import { ParkingRequestBookingDataService } from '../../services/parking-request-booking-data.service';
import { ParkingRequestDataLoaderService } from '../../services/parking-request-data-loader.service';
import {
  ComponentCallbacks,
  ComponentStateGetters,
  ComponentStateSetters,
  ParkingRequestEventHandlerService
} from '../../services/parking-request-event-handler.service';
import { ParkingRequestFormMappingService } from '../../services/parking-request-form-mapping.service';
import { FormFieldSetters, InitialFormValues, ParkingRequestFormResetService } from '../../services/parking-request-form-reset.service';
import { OriginalFormValues, ParkingRequestFormStateService } from '../../services/parking-request-form-state.service';
import { ParkingRequestFormSubmissionService } from '../../services/parking-request-form-submission.service';
import { ParkingRequestFormValidationService } from '../../services/parking-request-form-validation.service';
import { ParkingRequestFormService } from '../../services/parking-request-form.service';
import { CurrentFormValues, ParkingRequestPayloadBuilderService } from '../../services/parking-request-payload-builder.service';
import { ParkingRequestReadonlyStateService, ReadonlyState } from '../../services/parking-request-readonly-state.service';
import { ParkingRequestService } from '../../services/parking-request.service';
import { ParkingRequestBookingFieldComponent } from './parking-request-booking-field.component';
import { ParkingRequestDateFieldsComponent } from './parking-request-date-fields.component';
import { ParkingRequestParkingPlaceFieldComponent } from './parking-request-parking-place-field.component';

@Component({
  selector: 'app-parking-request-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DatePickerModule,
    CheckboxModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    CountrySelectComponent,
    ParkingRequestBookingFieldComponent,
    ParkingRequestParkingPlaceFieldComponent,
    ParkingRequestDateFieldsComponent
  ],
  templateUrl: './parking-request-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    MessageService,
    ConfirmationService,
    ParkingRequestFormStateService,
    ParkingRequestPayloadBuilderService,
    ParkingRequestFormResetService,
    ParkingRequestEventHandlerService,
    ParkingRequestDataLoaderService,
    ParkingRequestFormSubmissionService
  ]
})
export class ParkingRequestFormComponent implements OnInit {
  // Input signals
  initialBookingId = input<string | null>(null);
  initialParkingPlaceId = input<string | null>(null);
  initialStartDate = input<Date | null>(null);
  editMode = input<boolean>(false);
  parkingRequestId = input<string | null>(null);

  // Output events
  @Output() saved = new EventEmitter<IParkingRequestModel>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() loadError = new EventEmitter<any>();

  // Form state signals
  loading = signal<boolean>(false);
  bookingLoading = signal<boolean>(false);
  submitting = signal<boolean>(false);

  // Data signals
  bookings = signal<IBookingModel[]>([]);
  filteredBookings = signal<IBookingModel[]>([]);
  parkingPlaceOptions = signal<UiSelectItem<IFlatModel>[]>([]);
  loadingMoreBookings = signal<boolean>(false);

  // Pending parking place ID for timing issues
  private pendingParkingPlaceId: string | null = null;

  // Template-driven form values (pure template-driven approach)
  bookingValue: string | null = null;
  startDateValue: Date | null = null;
  endDateValue: Date | null = null;

  // Date constraints for booking range validation
  minDate: Date | null = null;
  maxDate: Date | null = null;
  parkingPlaceValue: string | null = null; // Changed from signal to plain property
  disabledParkingPlaceValue: boolean = false;
  paidValue: boolean = false;
  licensePlateNotKnownValue: boolean = false;
  licensePlateValue: string = '';
  licensePlateCountryCodeValue: string | null = null;

  // Full objects for child components (when not in current options lists)
  selectedBookingObject: IBookingModel | null = null;
  selectedParkingPlaceObject: IFlatModel | null = null; // Changed from signal to plain property

  // Date auto-correction notifications
  startDateCorrected = signal<boolean>(false);
  endDateCorrected = signal<boolean>(false);
  dateCorrectionsMessage = signal<string>('');

  // Today's date for filtering
  today = new Date();

  // ViewChild for date fields component
  @ViewChild('dateFieldsComponent') dateFieldsComponent!: ParkingRequestDateFieldsComponent;

  // Change detection via service
  get hasChanges() {
    return this.formStateService.hasChanges;
  }

  // Inject services
  private selectItemLoader = inject(SelectItemLoaderService);
  private readonly bookingDataService = inject(ParkingRequestBookingDataService);
  private formMappingService = inject(ParkingRequestFormMappingService);
  private formValidationService = inject(ParkingRequestFormValidationService);
  private formService = inject(ParkingRequestFormService);
  private parkingRequestService = inject(ParkingRequestService);
  private formStateService = inject(ParkingRequestFormStateService);
  private payloadBuilderService = inject(ParkingRequestPayloadBuilderService);
  private formResetService = inject(ParkingRequestFormResetService);
  private eventHandlerService = inject(ParkingRequestEventHandlerService);
  private parkingRequestClient = inject(ParkingRequestClientService);
  private readonly readonlyStateService = inject(ParkingRequestReadonlyStateService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private translate = inject(TranslateService);
  private log = inject(LogService);
  private readonly hostActivityLog = inject(HostActivityLogService);

  // Readonly state signals
  readonlyState = signal<ReadonlyState>({
    booking: false,
    parkingPlace: false,
    startDate: false,
    endDate: false
  });

  constructor() {
    // DEBUG: Log component construction
    this.log.debug('ParkingRequestFormComponent constructor called');
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  /**
   * Load initial data for the form
   */
  private loadInitialData(): void {
    this.loading.set(true);

    // DEBUG: Log that we're starting to load initial data
    this.log.debug('Starting loadInitialData()');

    // Load parking places from the select item loader
    this.loadParkingPlaces();

    // Load initial bookings with default filters
    this.log.debug('About to call loadBookings()');
    this.loadBookings();

    // If in edit mode, load the parking request data
    if (this.editMode() && this.parkingRequestId()) {
      this.loadParkingRequestData();
    } else {
      // For create mode, apply initial values if provided
      this.applyInitialValues();
      this.loading.set(false);
    }
  }

  /**
   * Load parking places from the select item loader
   */
  private loadParkingPlaces(): void {
    this.selectItemLoader.getParkingPlaceSelectItems().subscribe(
      (items: any[]) => {
        this.parkingPlaceOptions.set(items);

        // If we have a pending parking place ID to set (from edit mode), set it now
        if (this.pendingParkingPlaceId) {
          this.setParkingPlaceValue(this.pendingParkingPlaceId);
          this.pendingParkingPlaceId = null;
        }
      },
      (error: Error) => {
        this.log.error('Failed to load parking places', error);
      }
    );
  }

  /**
   * Apply initial values from inputs (for create mode)
   */
  private applyInitialValues(): void {
    if (this.initialBookingId()) {
      // Load booking data and set it in the template-driven form
      this.loadBooking(this.initialBookingId() as string);
    }

    if (this.initialParkingPlaceId()) {
      // Find parking place in options and set it
      const parkingPlace = this.parkingPlaceOptions().find((item) => item.value === this.initialParkingPlaceId());

      if (parkingPlace?.model) {
        this.parkingPlaceValue = parkingPlace.value;
      }
    }
  }

  /**
   * Load parking request data for edit mode
   */
  private loadParkingRequestData(): void {
    if (!this.parkingRequestId()) {
      this.loading.set(false);
      return;
    }

    this.loadParkingRequest(this.parkingRequestId() as string);
  }

  /**
   * Load parking request data from the API
   * @param parkingRequestId ID of the parking request to load
   */
  private loadParkingRequest(parkingRequestId: string): void {
    this.formService.loadParkingRequestForEdit(parkingRequestId, this.messageService).subscribe({
      next: (parkingRequest: IParkingRequestModel) => {
        if (parkingRequest) {
          this.loadExistingParkingRequest(parkingRequest);
        } else {
          this.log.warn('No parking request data received from API');
          this.loading.set(false);
          this.loadError.emit(null);
        }
      },
      error: (error) => {
        this.log.error('Error loading parking request from API:', error);
        this.loading.set(false);

        // Check if this is a 404 error by examining the error pattern
        // HTTP 404 errors may be wrapped in TypeError due to error handling chain
        const isLikelyParkingRequest404 =
          error &&
          (error.message?.includes('Cannot read properties of undefined') || error.name === 'TypeError') &&
          this.editMode() &&
          this.parkingRequestId();

        if (isLikelyParkingRequest404) {
          // Create a clean 404 error object for the container
          const notFoundError = {
            isParkingRequest404: true,
            status: 404,
            message: 'Parking request not found',
            originalError: error
          };
          this.loadError.emit(notFoundError);
        } else {
          this.log.debug('Not detected as 404, emitting original error');
          this.loadError.emit(error);
        }
      }
    });
  }

  /**
   * Load existing parking request data into the form
   * @param parkingRequest Parking request data to load
   */
  private loadExistingParkingRequest(parkingRequest: IParkingRequestModel): void {
    // Use the mapping service to populate template-driven form fields
    this.formMappingService.mapModelToForm(parkingRequest, null, this);

    // Handle booking reference (needs to be loaded separately since we only have IRI)
    if (parkingRequest.booking) {
      let bookingId: string | null = null;
      if (typeof parkingRequest.booking === 'string') {
        bookingId = parkingRequest.booking;
      } else if (typeof parkingRequest.booking === 'object' && parkingRequest.booking['@id']) {
        bookingId = parkingRequest.booking['@id'];
      }

      if (bookingId) {
        if (parkingRequest.start_date) {
          this.loadBookingForParkingRequest(bookingId, new Date(parkingRequest.start_date));
        } else {
          this.loadBookingForParkingRequest(bookingId);
        }
      } else {
        this.log.warn('[ParkingRequestForm] No valid booking ID found in:', parkingRequest.booking);
      }
    }

    // Handle parking place reference
    if (parkingRequest.parkingPlace) {
      let parkingPlaceId: string | null = null;
      let parkingPlaceObject: IFlatModel | null = null;

      if (typeof parkingRequest.parkingPlace === 'string') {
        parkingPlaceId = parkingRequest.parkingPlace;
      } else if (typeof parkingRequest.parkingPlace === 'object' && parkingRequest.parkingPlace['@id']) {
        parkingPlaceId = parkingRequest.parkingPlace['@id'];
        parkingPlaceObject = parkingRequest.parkingPlace as IFlatModel;
      } else {
        this.log.warn('[ParkingRequestForm] Parking place format not recognized:', parkingRequest.parkingPlace);
      }

      // Store the full parking place object if available
      if (parkingPlaceObject) {
        this.selectedParkingPlaceObject = parkingPlaceObject;
      }

      if (parkingPlaceId) {
        // Try to set the parking place value
        this.setParkingPlaceValue(parkingPlaceId);

        // If parking place options are not loaded yet, store the ID for later
        if (this.parkingPlaceOptions().length === 0) {
          this.pendingParkingPlaceId = parkingPlaceId;
        }
      } else {
        this.log.warn('[ParkingRequestForm] No valid parking place ID found');
      }
    }

    // Update readonly state based on the loaded parking request
    this.updateReadonlyState();

    // Note: captureOriginalValues() will be called after async booking loading completes
    // If no booking to load, capture original values now
    if (!parkingRequest.booking) {
      this.captureOriginalValues();
      this.loading.set(false);
    }
  }

  /**
   * Capture original values for change detection
   */
  private captureOriginalValues(): void {
    const values: OriginalFormValues = {
      bookingValue: this.bookingValue,
      startDateValue: this.startDateValue ? new Date(this.startDateValue) : null,
      endDateValue: this.endDateValue ? new Date(this.endDateValue) : null,
      parkingPlaceValue: this.parkingPlaceValue,
      disabledParkingPlaceValue: this.disabledParkingPlaceValue,
      paidValue: this.paidValue,
      licensePlateNotKnownValue: this.licensePlateNotKnownValue,
      licensePlateValue: this.licensePlateValue,
      licensePlateCountryCodeValue: this.licensePlateCountryCodeValue
    };

    this.formStateService.captureOriginalValues(values);
  }

  /**
   * Detect if form values have changed from original values
   */
  private detectChanges(): void {
    if (!this.editMode()) {
      return;
    }

    const currentValues: OriginalFormValues = {
      bookingValue: this.bookingValue,
      startDateValue: this.startDateValue,
      endDateValue: this.endDateValue,
      parkingPlaceValue: this.parkingPlaceValue,
      disabledParkingPlaceValue: this.disabledParkingPlaceValue,
      paidValue: this.paidValue,
      licensePlateNotKnownValue: this.licensePlateNotKnownValue,
      licensePlateValue: this.licensePlateValue,
      licensePlateCountryCodeValue: this.licensePlateCountryCodeValue
    };

    this.formStateService.detectChanges(currentValues);
  }

  /**
   * Update readonly state based on current form conditions
   */
  private updateReadonlyState(): void {
    const newReadonlyState = this.readonlyStateService.determineReadonlyState(this.editMode());
    this.readonlyState.set(newReadonlyState);
  }

  /**
   * Set parking place value in the form
   * @param parkingPlaceId The parking place ID to set
   */
  private setParkingPlaceValue(parkingPlaceId: string): void {
    const parkingPlace = this.parkingPlaceOptions().find((item) => item.value === parkingPlaceId);

    // Always set the parking place value, regardless of whether it's in current options
    // The child component will handle fallback display if not in options
    this.parkingPlaceValue = parkingPlaceId;

    if (!parkingPlace) {
      this.log.warn('Parking place not found in options for ID:', parkingPlaceId);
    }
  }

  /**
   * Handle start date change from template-driven form
   * @param date The selected date
   */
  onStartDateChange(date: Date | null): void {
    this.eventHandlerService.onStartDateChange(date, this.getComponentStateSetters());
    this.detectChanges();
  }

  /**
   * Handle end date change from template-driven form
   * @param date The selected date
   */
  onEndDateChange(date: Date | null): void {
    this.eventHandlerService.onEndDateChange(date, this.getComponentStateSetters());
    this.detectChanges();
  }

  /**
   * Handle booking selection change from template-driven form
   * @param bookingIri The selected booking IRI
   */
  onBookingChange(bookingIri: string | null): void {
    this.bookingValue = bookingIri;

    // Find the actual booking object from the IRI
    const booking = this.findBookingByIri(bookingIri);

    const constraints = this.formValidationService.getDateConstraints(booking);
    if (constraints) {
      this.minDate = constraints.minDate;
      this.maxDate = constraints.maxDate;

      // Update date fields component with new constraints (primitive approach)
      if (this.dateFieldsComponent) {
        this.dateFieldsComponent.updateDateConstraints(this.minDate, this.maxDate);
      }
    }

    // Apply smart date logic when booking is selected
    this.applySmartDateLogic(booking);

    this.detectChanges();
  }

  /**
   * Handle parking place selection change from template-driven form
   * @param parkingPlaceIri The selected parking place IRI
   */
  onParkingPlaceChange(parkingPlaceIri: string | null): void {
    this.parkingPlaceValue = parkingPlaceIri;
    this.detectChanges();
  }

  /**
   * Handle disabled parking place checkbox change from template-driven form
   * @param value The checkbox value
   */
  onDisabledParkingPlaceChange(value: boolean): void {
    this.disabledParkingPlaceValue = value;
    this.detectChanges();
  }

  /**
   * Handle paid checkbox change from template-driven form
   * @param value The checkbox value
   */
  onPaidChange(value: boolean): void {
    this.paidValue = value;
    this.detectChanges();
  }

  /**
   * Handle license plate not known checkbox change from template-driven form
   * @param value The checkbox value
   */
  onLicensePlateNotKnownChange(value: boolean): void {
    this.licensePlateNotKnownValue = value;
    this.detectChanges();
  }

  /**
   * Handle license plate input change from template-driven form
   * @param value The license plate value
   */
  onLicensePlateChange(value: string): void {
    this.licensePlateValue = value;
    this.detectChanges();
  }

  /**
   * Handle license plate country code change from template-driven form
   * @param value The country code value
   */
  onLicensePlateCountryCodeChange(value: string | null): void {
    this.licensePlateCountryCodeValue = value;
    this.detectChanges();
  }

  /**
   * Clear license plate fields when "not known" is checked
   */
  private clearLicensePlateFields(): void {
    this.licensePlateValue = '';
    this.licensePlateCountryCodeValue = null;
  }

  /**
   * Update date constraints for date pickers
   */
  private updateDateConstraints(minDate: Date | null, maxDate: Date | null): void {
    // Update date field constraints via ViewChild
    if (this.dateFieldsComponent) {
      this.dateFieldsComponent.updateDateConstraints(minDate, maxDate);
    }
  }

  /**
   * Check if the template-driven form is valid
   * @returns true if all required fields are valid
   */
  isFormValid(): boolean {
    // Required fields validation
    const hasBooking = !!this.bookingValue;
    const hasStartDate = !!this.startDateValue;
    const hasEndDate = !!this.endDateValue;
    const hasParkingPlace = !!this.parkingPlaceValue;

    // Date validation from child component
    const dateFieldsValid = !this.dateFieldsComponent?.hasValidationErrors();

    // License plate is never required (removed validation)
    return hasBooking && hasStartDate && hasEndDate && hasParkingPlace && dateFieldsValid;
  }

  /**
   * Load booking data for a parking request
   * @param bookingId ID of the booking to load
   * @param startDate Start date of the parking request (optional)
   */
  private loadBookingForParkingRequest(bookingId: string, startDate?: Date): void {
    // Set booking loading state
    this.bookingLoading.set(true);

    this.formService.loadBookingDetails(bookingId, this.messageService).subscribe({
      next: (booking: IBookingModel) => {
        if (booking) {
          // Store the full booking object for child component
          this.selectedBookingObject = booking;

          // Set the booking IRI (not the full object) to match our form structure
          const bookingIri = booking['@id'] || bookingId;
          this.bookingValue = bookingIri;

          // Set start date if provided, otherwise use booking start date
          if (startDate) {
            this.startDateValue = startDate;
          } else if (booking.start_date) {
            this.startDateValue = new Date(booking.start_date);
          }

          // Only set end date from booking if parking request doesn't have its own end date
          // This preserves the parking request's actual end date when it exists
          if (!this.endDateValue && booking.end_date) {
            this.endDateValue = new Date(booking.end_date);
          }

          // Set date constraints for the loaded booking (critical for edit mode)
          const constraints = this.formValidationService.getDateConstraints(booking);
          if (constraints) {
            this.minDate = constraints.minDate;
            this.maxDate = constraints.maxDate;

            // Update date fields component with new constraints
            if (this.dateFieldsComponent) {
              this.dateFieldsComponent.updateDateConstraints(this.minDate, this.maxDate);
            }
          }

          // Now that booking is loaded, capture original values for change detection
          this.captureOriginalValues();
        } else {
          this.log.warn('No booking data received');
          this.captureOriginalValues(); // Capture even if no booking data
        }

        this.bookingLoading.set(false);
        this.loading.set(false); // Mark overall loading as complete
      },
      error: (error) => {
        this.log.error('Error loading booking:', error);
        this.bookingLoading.set(false);
        this.loading.set(false);
      }
    });
  }

  /**
   * Load booking data from the API
   * @param bookingId ID of the booking to load
   */
  private loadBooking(bookingId: string): void {
    this.formService.loadBookingForParkingRequest(bookingId, this.messageService).subscribe({
      next: (booking: IBookingModel) => {
        if (booking && booking.end_date) {
          const endDate = new Date(booking.end_date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (endDate >= today) {
            // Set template-driven form value
            this.bookingValue = booking['@id'] || bookingId;
          }
          // Warning message is handled by the service
        }
      },
      error: () => {
        // Error handling is done by the service
      }
    });
  }

  /**
   * Find booking object by IRI from the current bookings list
   * @param iri The booking IRI to search for
   * @returns The booking object or null if not found
   */
  private findBookingByIri(iri: string | null): IBookingModel | null {
    if (!iri) return null;
    return this.bookings().find((booking) => booking['@id'] === iri) || null;
  }

  /**
   * Apply smart date logic when booking is selected
   * - Set defaults if no dates are set
   * - Auto-correct dates that are outside booking range
   * - Show notifications for auto-corrections
   */
  private applySmartDateLogic(booking: IBookingModel | null): void {
    if (!booking || !booking.start_date || !booking.end_date) {
      return;
    }

    const bookingStartDate = new Date(booking.start_date);
    const bookingEndDate = new Date(booking.end_date);
    let correctionsMade = false;
    const corrections: string[] = [];

    // Reset correction flags
    this.startDateCorrected.set(false);
    this.endDateCorrected.set(false);

    // Handle start date
    if (!this.startDateValue) {
      // No value set - use booking start date as default
      this.startDateValue = new Date(bookingStartDate);
    } else if (this.startDateValue < bookingStartDate || this.startDateValue > bookingEndDate) {
      // Value is outside booking range - auto-correct
      const originalDate = new Date(this.startDateValue);
      this.startDateValue = new Date(bookingStartDate);
      this.startDateCorrected.set(true);
      correctionsMade = true;
      const startCorrectionMessage = this.translate.instant('parking.form.startDateCorrected', {
        originalDate: originalDate.toLocaleDateString(),
        newDate: this.startDateValue.toLocaleDateString()
      });
      corrections.push(startCorrectionMessage);
    }

    // Handle end date
    if (!this.endDateValue) {
      // No value set - use booking end date as default
      this.endDateValue = new Date(bookingEndDate);
    } else if (this.endDateValue < bookingStartDate || this.endDateValue > bookingEndDate) {
      // Value is outside booking range - auto-correct
      const originalDate = new Date(this.endDateValue);
      this.endDateValue = new Date(bookingEndDate);
      this.endDateCorrected.set(true);
      correctionsMade = true;
      const endCorrectionMessage = this.translate.instant('parking.form.endDateCorrected', {
        originalDate: originalDate.toLocaleDateString(),
        newDate: this.endDateValue.toLocaleDateString()
      });
      corrections.push(endCorrectionMessage);
    }

    // Set correction message if any corrections were made
    if (correctionsMade) {
      this.dateCorrectionsMessage.set(corrections.join(', '));
      // Clear message after 10 seconds
      setTimeout(() => {
        this.dateCorrectionsMessage.set('');
        this.startDateCorrected.set(false);
        this.endDateCorrected.set(false);
      }, 10000);
    }
  }

  /**
   * Load bookings with filtering and pagination
   * @param reset Whether to reset the pagination or append results
   */
  loadBookings(reset: boolean = true): void {
    this.log.debug('loadBookings() called with reset:', reset);
    
    const subscription = this.bookingDataService.loadBookings(reset);
    this.log.debug('Got observable from service:', subscription);
    
    subscription.subscribe({
      next: (bookings: IBookingModel[]) => {
        // DEBUG: Log what we're receiving in the component
        this.log.debug('ParkingRequestForm received bookings:', bookings);
        this.log.debug('Bookings length:', bookings.length);
        
        // DEBUG: Check first booking structure
        if (bookings.length > 0) {
          this.log.debug('First booking sample:', bookings[0]);
          this.log.debug('First booking @id:', bookings[0]['@id']);
          this.log.debug('First booking name:', bookings[0].name);
        }

        this.bookings.set(bookings);
        this.filteredBookings.set(bookings);
        this.loading.set(false);
        
        // DEBUG: Verify signals are set
        this.log.debug('Bookings signal value:', this.bookings());
        this.log.debug('FilteredBookings signal value:', this.filteredBookings());
      },
      error: (error: Error) => {
        this.log.error('loadBookings() error in component:', error);
        this.loading.set(false);
      },
      complete: () => {
        this.log.debug('loadBookings() observable completed');
      }
    });

    // Subscribe to loading state
    this.bookingDataService.loadingMoreBookings$.subscribe((loading) => {
      this.loadingMoreBookings.set(loading);
    });
  }

  /**
   * Handle booking search filter change
   * @param event Search event
   */
  onBookingSearchChange(event: any): void {
    this.bookingDataService.searchBookings(event.filter || '');
    
    // Subscribe to the updated filtered results from the service
    this.bookingDataService.bookings$.subscribe((bookings: IBookingModel[]) => {
      this.log.debug('Search results received in component:', bookings);
      this.log.debug('Search results length:', bookings.length);
      
      this.bookings.set(bookings);
      this.filteredBookings.set(bookings);
    });
  }

  /**
   * Load more bookings when scrolling
   * @param event Scroll event
   */
  onBookingScrollToBottom(): void {
    this.bookingDataService.loadMoreBookings();
  }

  /**
   * Validate dates to ensure they are within booking date range
   */
  validateDates(): boolean {
    // Template-driven form validation using component state
    const booking = this.findBookingByIri(this.bookingValue);
    if (!booking || !this.startDateValue || !this.endDateValue) {
      return false;
    }

    const bookingStart = new Date(booking.start_date!);
    const bookingEnd = new Date(booking.end_date!);

    if (
      this.startDateValue < bookingStart ||
      this.startDateValue > bookingEnd ||
      this.endDateValue < bookingStart ||
      this.endDateValue > bookingEnd
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('parking.form.invalidDateRange'),
        detail: this.translate.instant('parking.form.dateRangeOutsideBooking')
      });
      return false;
    }

    return true;
  }

  /**
   * Handle form submission
   */
  onSubmit(): void {
    if (!this.isFormValid()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.error'),
        detail: this.translate.instant('parking.form.validationError')
      });
      return;
    }

    this.submitting.set(true);

    if (this.editMode()) {
      // Edit mode: send only changed fields using PATCH
      this.updateParkingRequest();
    } else {
      // Create mode: send full payload using POST
      this.createParkingRequest();
    }
  }

  /**
   * Create new parking request using POST
   */
  private createParkingRequest(): void {
    const payload = this.buildFullPayload();
    const after = this.activitySnapshotOf(this.getCurrentFormValues());

    this.parkingRequestClient.createItem(payload).subscribe({
      next: (savedRequest) => {
        this.submitting.set(false);
        this.hostActivityLog.log('parking_request_create', parkingRequestCreateMetadata({ parkingRequestId: savedRequest?.id ?? null, after }));

        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.success'),
          detail: this.translate.instant('parking.form.saveSuccess')
        });
        this.saved.emit(savedRequest);
      },
      error: (error) => {
        this.submitting.set(false);
        this.log.error('Failed to create parking request', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.error'),
          detail: this.translate.instant('parking.form.saveFailed')
        });
      }
    });
  }

  /**
   * Update existing parking request using PATCH with only changed fields
   */
  private updateParkingRequest(): void {
    const payload = this.buildChangedFieldsPayload();
    const originalValues = this.formStateService.getOriginalValues();
    const before = originalValues ? this.activitySnapshotOf(originalValues) : null;
    const after = this.activitySnapshotOf(this.getCurrentFormValues());

    this.parkingRequestClient.updateItem(payload).subscribe({
      next: (savedRequest) => {
        this.submitting.set(false);
        const fields = Object.keys(payload ?? {});
        const events = parkingRequestUpdateEvents({ parkingRequestId: Number(this.parkingRequestId()), before, after, fields });
        events.forEach((event) => this.hostActivityLog.log(event.action, event.metadata));

        // Reload form with the actual saved data from API response
        this.loadExistingParkingRequest(savedRequest);

        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.success'),
          detail: this.translate.instant('parking.form.saveSuccess')
        });

        this.saved.emit(savedRequest);
      },
      error: (error) => {
        this.submitting.set(false);
        this.log.error('Failed to update parking request', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.error'),
          detail: this.translate.instant('parking.form.saveFailed')
        });
      }
    });
  }

  /**
   * Cancel form and navigate back
   */
  onCancel(): void {
    this.cancelled.emit();
  }

  /**
   * Get form field setters for the reset service
   */
  private getFormFieldSetters(): FormFieldSetters {
    return {
      setBookingValue: (value: string | null) => {
        this.bookingValue = value;
      },
      setStartDateValue: (value: Date | null) => {
        this.startDateValue = value;
      },
      setEndDateValue: (value: Date | null) => {
        this.endDateValue = value;
      },
      setParkingPlaceValue: (value: string | null) => {
        this.parkingPlaceValue = value;
      },
      setDisabledParkingPlaceValue: (value: boolean) => {
        this.disabledParkingPlaceValue = value;
      },
      setPaidValue: (value: boolean) => {
        this.paidValue = value;
      },
      setLicensePlateNotKnownValue: (value: boolean) => {
        this.licensePlateNotKnownValue = value;
      },
      setLicensePlateValue: (value: string) => {
        this.licensePlateValue = value;
      },
      setLicensePlateCountryCodeValue: (value: string | null) => {
        this.licensePlateCountryCodeValue = value;
      }
    };
  }

  /**
   * Get component state setters for the event handler service
   */
  private getComponentStateSetters(): ComponentStateSetters {
    return {
      setStartDateValue: (value: Date | null) => {
        this.startDateValue = value;
      },
      setEndDateValue: (value: Date | null) => {
        this.endDateValue = value;
      },
      setBookingValue: (value: string | null) => {
        this.bookingValue = value;
      },
      setParkingPlaceValue: (value: string | null) => {
        this.parkingPlaceValue = value;
      },
      setDisabledParkingPlaceValue: (value: boolean) => {
        this.disabledParkingPlaceValue = value;
      },
      setPaidValue: (value: boolean) => {
        this.paidValue = value;
      },
      setLicensePlateNotKnownValue: (value: boolean) => {
        this.licensePlateNotKnownValue = value;
      },
      setLicensePlateValue: (value: string) => {
        this.licensePlateValue = value;
      },
      setLicensePlateCountryCodeValue: (value: string | null) => {
        this.licensePlateCountryCodeValue = value;
      }
    };
  }

  /**
   * Get component state getters for the event handler service
   */
  private getComponentStateGetters(): ComponentStateGetters {
    return {
      getBookingValue: () => this.bookingValue,
      getLicensePlateNotKnownValue: () => this.licensePlateNotKnownValue
    };
  }

  /**
   * Get component callbacks for the event handler service
   */
  private getComponentCallbacks(): ComponentCallbacks {
    return {
      loadBookingForParkingRequest: (bookingIri: string) => this.loadBookingForParkingRequest(bookingIri),
      clearLicensePlateFields: () => this.clearLicensePlateFields(),
      updateDateConstraints: (minDate: Date | null, maxDate: Date | null) => this.updateDateConstraints(minDate, maxDate),
      onBookingSearchChange: (event: any) => this.onBookingSearchChange(event)
    };
  }

  /**
   * Get initial form values from component inputs
   */
  private getInitialFormValues(): InitialFormValues {
    return {
      initialBookingId: this.initialBookingId(),
      initialParkingPlaceId: this.initialParkingPlaceId(),
      initialStartDate: this.initialStartDate()
    };
  }

  /**
   * Reset form to initial state
   */
  onReset(): void {
    this.formResetService.performReset(
      this.editMode(),
      this.formStateService.getOriginalValues(),
      this.getInitialFormValues(),
      this.getFormFieldSetters(),
      this.hasChanges,
      (id: string) => this.setParkingPlaceValue(id)
    );
  }

  /**
   * Get current form values for payload building
   */
  private getCurrentFormValues(): CurrentFormValues {
    return {
      bookingValue: this.bookingValue,
      startDateValue: this.startDateValue,
      endDateValue: this.endDateValue,
      parkingPlaceValue: this.parkingPlaceValue,
      disabledParkingPlaceValue: this.disabledParkingPlaceValue,
      paidValue: this.paidValue,
      licensePlateNotKnownValue: this.licensePlateNotKnownValue,
      licensePlateValue: this.licensePlateValue,
      licensePlateCountryCodeValue: this.licensePlateCountryCodeValue
    };
  }

  /**
   * Build payload with only changed fields for efficient PATCH requests
   */
  private buildChangedFieldsPayload(): any {
    const originalValues = this.formStateService.getOriginalValues();
    if (!originalValues || !this.editMode()) {
      // For create mode, return all fields
      return this.buildFullPayload();
    }

    try {
      return this.payloadBuilderService.buildChangedFieldsPayload(this.getCurrentFormValues(), originalValues, this.parkingRequestId());
    } catch (error) {
      this.log.error('Error building changed fields payload:', error);
      throw error;
    }
  }

  /**
   * Build full payload for create operations
   */
  private buildFullPayload(): any {
    return this.payloadBuilderService.buildFullPayload(this.getCurrentFormValues());
  }

  /**
   * The form values as the host activity log sees them (no licence plate, only whether one was given),
   * with the chosen booking's apartment flats
   */
  private activitySnapshotOf(values: CurrentFormValues): ParkingActivitySnapshot {
    const bookingFlats = activityFlatsOfModels(this.bookingOfIri(values.bookingValue)?.flats);
    return parkingFormSnapshotOf({ values, places: this.parkingPlaceOptions(), bookingFlats });
  }

  /**
   * The booking object behind a form IRI: the edited request's loaded booking, else one from the options list
   * @param iri The booking IRI
   * @returns The booking, or null when neither has it
   */
  private bookingOfIri(iri: string | null): IBookingModel | null {
    if (iri && this.selectedBookingObject?.['@id'] === iri) return this.selectedBookingObject;
    return this.findBookingByIri(iri);
  }

  /**
   * Delete parking request with confirmation
   * Uses the same confirmation pattern as the parking requests table
   */
  onDelete(): void {
    if (!this.parkingRequestId()) {
      this.log.warn('Cannot delete parking request: no ID available');
      return;
    }

    this.log.debug('Delete request initiated for parking request ID:', this.parkingRequestId());

    this.confirmationService.confirm({
      message: this.translate.instant('parking.requests.delete.confirmMessage'),
      header: this.translate.instant('common.delete.confirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('common.yesDelete'),
      rejectLabel: this.translate.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        const requestId = parseInt(this.parkingRequestId()!, 10);

        if (isNaN(requestId)) {
          this.log.error('Invalid parking request ID for deletion:', this.parkingRequestId());
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.error'),
            detail: this.translate.instant('parking.requests.delete.error')
          });
          return;
        }

        this.submitting.set(true);
        const before = this.activitySnapshotOf(this.formStateService.getOriginalValues() ?? this.getCurrentFormValues());

        this.parkingRequestService.deleteParkingRequest(requestId).subscribe({
          next: (deleted: boolean) => {
            if (deleted) {
              const metadata = parkingRequestDeleteMetadata({ parkingRequestId: requestId, before, origin: 'parking_form' });
              this.hostActivityLog.log('parking_request_delete', metadata);
            }
            if (!deleted) {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('common.error'),
                detail: this.translate.instant('parking.requests.delete.error')
              });
              this.submitting.set(false);
              return;
            }
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('common.success'),
              detail: this.translate.instant('parking.requests.delete.success')
            });

            // Navigate back to parking overview after successful deletion
            this.cancelled.emit();
          },
          error: (error: unknown) => {
            this.log.error('Error deleting parking request:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.error'),
              detail: this.translate.instant('parking.requests.delete.error')
            });
            this.submitting.set(false);
          }
        });
      }
    });
  }
}
